// Isolated mock layer for the Agent-Tender feature (D-007).
//
// Active ONLY when REACT_APP_AGENT_TENDER_MOCK === "1". Lets the whole
// Agent-Tender UI be built and demonstrated before the backend endpoints land,
// against the shapes agreed in TP-AGENT-TENDER/API-CONTRACT.md.
//
// CRITICAL (Brief Rule 4): none of this mock data exposes a club to the FEED
// or public surfaces. `posted_agency` + `responsible_agent` are the only
// identities in the feed. The club appears ONLY in the agent's own "mine"
// records (internal, Rule 8) and in the public verify summary (the club sees
// its OWN request).

const wait = (ms = 350) => new Promise((r) => setTimeout(r, ms));

const AGENCIES = [
  { name: "Elite Football Partners", logo_url: "" },
  { name: "Iberia Sports Management", logo_url: "" },
  { name: "Nordic Talent Agency", logo_url: "" },
];

const AGENTS = [
  { display_name: "Marco Devries", avatar: "" },
  { display_name: "Sofia Alvarez", avatar: "" },
  { display_name: "Jonas Lindqvist", avatar: "" },
];

const trust = (a, b, c, d) => ({ posted: a, verified: b, revoked: c, deals: d });

// Feed rows — mix of verified + unverified so verified-first sorting is visible.
const FEED = [
  {
    id: 101,
    source: "agent",
    tender_category: "player",
    position: "center_forward",
    other_staff_position: "",
    type_of_tender: "buy",
    recruiting_for: "professional",
    league_tier: 1,
    country: { name: "Spain", code: "es" },
    budget_band: "€1M - €3M",
    // Requirement profile (position spec — no club, Rule 4 safe).
    min_age: 20,
    max_age: 28,
    foot: "A",
    minimum_height: 182,
    matches_played_last_season: 25,
    matches_played_last_half_season: 13,
    experience: "5-7",
    language_skills: ["Spanish", "English"],
    only_eu_player: true,
    only_free_players: false,
    loan_with_option_to_buy: false,
    offering_additional_benefits: true,
    travel: true,
    housing: true,
    food_covered: false,
    // Object shape sent by the feed serializer: label ("<Country>:<Season>",
    // never the club) + ISO-8601 open/close with offset (closes = end of day).
    transfer_window: {
      label: "Spain:2025/2026",
      opens_at: "2026-06-15T00:00:00+00:00",
      closes_at: "2026-09-01T23:59:59.999999+00:00",
    },
    commission_split: "60 / 40",
    commission_split_note: "",
    verification_status: "club_verified",
    verified_at: "2026-06-30T10:00:00Z",
    posted_agency: AGENCIES[1],
    responsible_agent: AGENTS[1],
    trust_stats: { agent: trust(14, 9, 0, 3), agency: trust(52, 40, 1, 12) },
    created_on: "2026-06-28T09:00:00Z",
  },
  {
    id: 102,
    source: "agent",
    tender_category: "player",
    position: "left_winger",
    other_staff_position: "",
    type_of_tender: "loan",
    recruiting_for: "professional",
    league_tier: 2,
    country: { name: "England", code: "gb-eng" },
    budget_band: "Loan fee — €500k - €1M",
    min_age: 18,
    max_age: 24,
    foot: "R",
    minimum_height: 178,
    experience: "2-3",
    language_skills: ["English"],
    only_eu_player: false,
    only_free_players: false,
    loan_with_option_to_buy: true,
    transfer_window: {
      label: "England:2025/2026",
      opens_at: "2026-06-14T00:00:00+00:00",
      closes_at: "2026-09-01T23:59:59.999999+00:00",
    },
    commission_split: "",
    commission_split_note: "Open to discussion",
    verification_status: "club_verified",
    verified_at: "2026-06-29T14:00:00Z",
    posted_agency: AGENCIES[0],
    responsible_agent: AGENTS[0],
    trust_stats: { agent: trust(8, 5, 0, 2), agency: trust(31, 22, 0, 7) },
    created_on: "2026-06-27T12:00:00Z",
  },
  {
    id: 103,
    source: "agent",
    tender_category: "staff",
    staff_role: "sporting_director",
    position: "",
    other_staff_position: "",
    type_of_tender: "full_time",
    recruiting_for: "",
    league_tier: 1,
    country: { name: "Germany", code: "de" },
    budget_band: "€15k - €30k",
    // Staff role: experience + languages, no player-only fields.
    experience: "7-10",
    language_skills: ["German", "English"],
    offering_additional_benefits: true,
    travel: true,
    housing: false,
    food_covered: false,
    // Fallback bucket when no window is resolvable for the club country:
    // label-only ("<Year>H1|H2"), both dates null. Proves the label fallback.
    transfer_window: { label: "2026H2", opens_at: null, closes_at: null },
    commission_split: "50 / 50",
    commission_split_note: "",
    verification_status: "unverified",
    verified_at: null,
    posted_agency: AGENCIES[2],
    responsible_agent: AGENTS[2],
    trust_stats: { agent: trust(3, 1, 0, 0), agency: trust(12, 6, 0, 1) },
    created_on: "2026-07-01T08:30:00Z",
    // Own-agency row: the feed shows a "Your request" marker here (no self-pitch).
    is_own_agency: true,
  },
  {
    id: 104,
    source: "agent",
    tender_category: "player",
    position: "goal_keeper",
    other_staff_position: "",
    type_of_tender: "trial",
    recruiting_for: "youth",
    league_tier: 3,
    country: { name: "Portugal", code: "pt" },
    // only_free_players → the backend's budget_band special "Free agents only".
    budget_band: "Free agents only",
    min_age: 16,
    max_age: 19,
    foot: "R",
    minimum_height: 188,
    only_free_players: true,
    trial_period_start_date: "2026-07-15",
    trial_period_end_date: "2026-07-29",
    transfer_window: {
      label: "Portugal:2026/2027",
      opens_at: "2026-07-01T00:00:00+00:00",
      closes_at: "2026-09-02T23:59:59.999999+00:00",
    },
    commission_split: "",
    commission_split_note: "",
    verification_status: "unverified",
    verified_at: null,
    posted_agency: AGENCIES[0],
    responsible_agent: AGENTS[0],
    trust_stats: { agent: trust(8, 5, 0, 2), agency: trust(31, 22, 0, 7) },
    created_on: "2026-06-25T16:00:00Z",
  },
];

// The agent's own tenders (internal view — club present, Rule 8).
const MINE = [
  {
    id: 201,
    tender_category: "player",
    position: "center_back",
    type_of_tender: "buy",
    recruiting_for: "professional",
    status: "active",
    verification_status: "club_verified",
    verified_at: "2026-06-30T10:00:00Z",
    country: { name: "Spain", code: "es" },
    league_tier: 1,
    budget_band: "€1M - €3M",
    min_age: 22,
    max_age: 30,
    foot: "L",
    minimum_height: 185,
    experience: "3-5",
    language_skills: ["Spanish"],
    only_eu_player: true,
    only_free_players: false,
    offering_additional_benefits: true,
    travel: true,
    housing: true,
    food_covered: true,
    commission_split: "60 / 40",
    transfer_window: {
      label: "Spain:2025/2026",
      opens_at: "2026-06-15T00:00:00+00:00",
      closes_at: "2026-09-01T23:59:59.999999+00:00",
    },
    club: { id: 9001, name: "Club Atlético Demo", country: "Spain", logo: "" },
    created_on: "2026-06-26T09:00:00Z",
  },
  {
    id: 202,
    tender_category: "player",
    position: "attacking_midfielder",
    type_of_tender: "loan",
    recruiting_for: "professional",
    status: "active",
    verification_status: "unverified",
    verified_at: null,
    country: { name: "Italy", code: "it" },
    league_tier: 2,
    budget_band: "Loan fee — €500k - €1M",
    min_age: 19,
    max_age: 26,
    foot: "R",
    minimum_height: 175,
    loan_with_option_to_buy: true,
    commission_split: "",
    transfer_window: {
      label: "Italy:2025/2026",
      opens_at: "2026-07-01T00:00:00+00:00",
      closes_at: "2026-09-01T23:59:59.999999+00:00",
    },
    club: { id: 9002, name: "Demo Calcio 1912", country: "Italy", logo: "" },
    created_on: "2026-07-01T11:00:00Z",
  },
  {
    id: 203,
    tender_category: "staff",
    staff_role: "coach",
    position: "",
    type_of_tender: "full_time",
    recruiting_for: "",
    status: "expired",
    verification_status: "superseded",
    verified_at: null,
    country: { name: "France", code: "fr" },
    league_tier: 1,
    budget_band: "€30k+",
    experience: "5-7",
    language_skills: ["French", "English"],
    offering_additional_benefits: true,
    commission_split: "50 / 50",
    // Expired staff tender with the label-only fallback window (dates null).
    transfer_window: { label: "2026H2", opens_at: null, closes_at: null },
    club: { id: 9003, name: "Demo Olympique", country: "France", logo: "" },
    created_on: "2026-05-20T09:00:00Z",
  },
];

const CLUBS = [
  { id: 9001, name: "Club Atlético Demo", country: "Spain", logo: "" },
  { id: 9002, name: "Demo Calcio 1912", country: "Italy", logo: "" },
  { id: 9003, name: "Demo Olympique", country: "France", logo: "" },
  { id: 9004, name: "Demo United FC", country: "England", logo: "" },
  { id: 9005, name: "Sporting Demo Lisboa", country: "Portugal", logo: "" },
];

// Responses received on the agent's OWN tenders (posting-side / internal view).
// A response is a Pitch on a source=agent tender; field names follow the
// existing PitchSerializer (player_name/player_position/player_image/
// transfer_fee/status) so the real backend can drop in unchanged. The
// responding agency + agent are shown to the posting side; the posting side is
// the club-owner here, so nothing is anonymized on this surface (Rule 8).
const respTender = (id, position, type) => ({
  id,
  tender_category: "player",
  position,
  other_staff_position: "",
  type_of_tender: type,
  recruiting_for: "professional",
});

let RESPONSES = [
  {
    id: 501,
    tender: respTender(201, "center_back", "buy"),
    player: {
      id: 71,
      name: "Lucas Moreau",
      position: "center_back",
      image: "",
      age: 24,
      foot: "Right",
      height: 188,
    },
    content: "Strong aerial presence, comfortable building from the back.",
    transfer_fee: "1200000",
    transfer_fee_currency: "EUR",
    free_agent: false,
    status: "active",
    is_shortlisted: false,
    responding_agency: { agency_id: 3, name: "Nordic Talent Agency", profile_picture_url: "" },
    responding_agent: { account_id: 33, name: "Jonas Lindqvist", profile_picture_url: "" },
    accepted_by: null,
    channel_id: null,
    created_at: "2026-07-02T09:00:00Z",
  },
  {
    id: 502,
    tender: respTender(201, "center_back", "buy"),
    player: {
      id: 72,
      name: "Diego Fuentes",
      position: "center_back",
      image: "",
      age: 27,
      foot: "Left",
      height: 185,
    },
    content: "Free agent, available immediately.",
    transfer_fee: "",
    transfer_fee_currency: "",
    free_agent: true,
    status: "active",
    is_shortlisted: true,
    responding_agency: { agency_id: 1, name: "Elite Football Partners", profile_picture_url: "" },
    responding_agent: { account_id: 11, name: "Marco Devries", profile_picture_url: "" },
    accepted_by: null,
    channel_id: null,
    created_at: "2026-07-02T14:30:00Z",
  },
  {
    id: 503,
    tender: respTender(202, "attacking_midfielder", "loan"),
    player: {
      id: 73,
      name: "Yuki Tanaka",
      position: "attacking_midfielder",
      image: "",
      age: 21,
      foot: "Right",
      height: 176,
    },
    content: "Creative number 10, open to a season-long loan.",
    transfer_fee: "300000",
    transfer_fee_currency: "EUR",
    free_agent: false,
    status: "rejected",
    is_shortlisted: false,
    responding_agency: { agency_id: 2, name: "Iberia Sports Management", profile_picture_url: "" },
    responding_agent: { account_id: 22, name: "Sofia Alvarez", profile_picture_url: "" },
    accepted_by: null,
    channel_id: null,
    created_at: "2026-07-01T18:00:00Z",
  },
];

const paginate = (rows, page = 1, pageSize = 12) => ({
  count: rows.length,
  next: page * pageSize < rows.length ? page + 1 : null,
  previous: page > 1 ? page - 1 : null,
  currentPage: page,
});

const applyFeedFilters = (rows, params) => {
  const p = params instanceof URLSearchParams ? params : new URLSearchParams(params || {});
  let out = [...rows];
  const cat = p.get("tender_category");
  const pos = p.get("position");
  const type = p.get("type_of_tender");
  const country = p.get("country");
  const tier = p.get("league_tier");
  const recruitingFor = p.get("recruiting_for");
  const status = p.get("status");
  const sortBy = p.get("sort_by");
  const verifiedOnly = p.get("verified") === "true";
  const search = (p.get("search") || "").toLowerCase();
  if (cat) out = out.filter((r) => r.tender_category === cat);
  if (pos) out = out.filter((r) => r.position === pos);
  if (type) out = out.filter((r) => r.type_of_tender === type);
  if (country) out = out.filter((r) => r.country?.name === country);
  if (tier) out = out.filter((r) => String(r.league_tier) === String(tier));
  if (recruitingFor) out = out.filter((r) => r.recruiting_for === recruitingFor);
  if (status && status !== "all") out = out.filter((r) => (r.status || "active") === status);
  if (verifiedOnly) out = out.filter((r) => r.verification_status === "club_verified");
  if (search) {
    out = out.filter(
      (r) =>
        r.position?.includes(search) ||
        r.posted_agency?.name?.toLowerCase().includes(search) ||
        r.responsible_agent?.display_name?.toLowerCase().includes(search),
    );
  }
  // NOTE (mock harness): market_value/continent/division/nationality/age params
  // are accepted for club-parity but not modeled in these fixtures, so they do
  // not narrow the list here. The real feed applies them server-side.
  //
  // Verified-first is ALWAYS the primary sort (mirrors the backend, which keeps
  // _vrank first regardless of sort_by); sort_by only flips the date tiebreaker.
  const ascending = sortBy === "created_asc" || sortBy === "oldest";
  out.sort((a, b) => {
    const av = a.verification_status === "club_verified" ? 0 : 1;
    const bv = b.verification_status === "club_verified" ? 0 : 1;
    if (av !== bv) return av - bv;
    const diff = new Date(a.created_on) - new Date(b.created_on);
    return ascending ? diff : -diff;
  });
  return out;
};

// Category-pill semantics — mirrors the backend category map: women is always
// dropped; players = tender_category=player; coaches/staffs/others split the
// staff requests by staff_role group (analysis + academy are in STAFFS).
const isWomen = (r) => r.tender_category === "player" && r.recruiting_for === "women";
const STAFFS_ROLES = new Set([
  "sporting_director", "technical_director", "head_of_scouting",
  "team_manager", "scout", "analysis", "academy",
]);
const matchCategory = (r, cat) => {
  if (cat === "players") return r.tender_category === "player";
  if (cat === "coaches") return r.tender_category === "staff" && r.staff_role === "coach";
  if (cat === "staffs")
    return r.tender_category === "staff" && STAFFS_ROLES.has(r.staff_role);
  if (cat === "others")
    return (
      r.tender_category === "staff" &&
      r.staff_role !== "coach" &&
      !STAFFS_ROLES.has(r.staff_role)
    );
  return true;
};
const computeTabCounts = (rows) => {
  const base = rows.filter((r) => !isWomen(r));
  return {
    players: base.filter((r) => matchCategory(r, "players")).length,
    coaches: base.filter((r) => matchCategory(r, "coaches")).length,
    staffs: base.filter((r) => matchCategory(r, "staffs")).length,
    others: base.filter((r) => matchCategory(r, "others")).length,
  };
};

export const agentTenderMock = {
  getFeed: async (params) => {
    await wait();
    const p = params instanceof URLSearchParams ? params : new URLSearchParams(params || {});
    // Counts come from the feed with every filter applied EXCEPT the category
    // (backend parity), then the category pill narrows the returned rows.
    const base = applyFeedFilters(FEED, params).filter((r) => !isWomen(r));
    const tabCounts = computeTabCounts(base);
    const cat = (p.get("category") || "").toLowerCase();
    const rows = cat ? base.filter((r) => matchCategory(r, cat)) : base;
    return { success: true, data: rows, pagination: paginate(rows), tabCounts };
  },
  getFeedOne: async (id) => {
    await wait();
    const row = FEED.find((r) => String(r.id) === String(id));
    return row
      ? { success: true, data: row }
      : { success: false, error: "Not found" };
  },
  getMine: async (params) => {
    await wait();
    const p = params instanceof URLSearchParams ? params : new URLSearchParams(params || {});
    const base = MINE.filter((r) => !isWomen(r));
    const tabCounts = computeTabCounts(base);
    const cat = (p.get("category") || "").toLowerCase();
    const rows = cat ? base.filter((r) => matchCategory(r, cat)) : base;
    return { success: true, data: rows, pagination: paginate(rows), tabCounts };
  },
  getClubs: async (q = "") => {
    await wait(200);
    const needle = q.toLowerCase();
    const data = CLUBS.filter((c) => c.name.toLowerCase().includes(needle));
    return { success: true, data };
  },
  create: async (payload) => {
    await wait();
    // Mirror the backend contract: exactly one of club_id / wyscout_club_id.
    const has = (v) => v !== undefined && v !== null && v !== "";
    const hasClub = has(payload.club_id);
    const hasWyscout = has(payload.wyscout_club_id);
    if (hasClub && hasWyscout) {
      return { success: false, status: 400, error: "Provide only one of club_id or wyscout_club_id." };
    }
    if (!hasClub && !hasWyscout) {
      return { success: false, status: 400, error: "Provide either club_id or wyscout_club_id." };
    }
    return { success: true, data: { id: Math.floor(Math.random() * 9000) + 1000, ...payload } };
  },
  update: async (id, payload) => {
    await wait();
    return { success: true, data: { id, ...payload } };
  },
  close: async (id) => {
    await wait();
    return { success: true, data: { id, status: "expired" } };
  },
  requestVerification: async (id, clubContactEmail) => {
    await wait();
    // In real life the token is server-generated. Mock the same response shape
    // the backend returns: link_url + qr_url carry a ?c= channel tag so the
    // public endpoints can log whether the club arrived via link or QR.
    const token = `mock-${id}-${Date.now().toString(36)}`;
    const base =
      typeof window !== "undefined" ? window.location.origin : "https://app.transferpitch.com";
    const url = `${base}/verify/agent-tender/${token}`;
    return {
      success: true,
      data: {
        verify_url: `${url}?c=link`,
        link_url: `${url}?c=link`,
        qr_url: `${url}?c=qr`,
        verification_id: Math.floor(Math.random() * 100000),
        // The address is optional: without one the token is still minted (QR +
        // link) and no mail is attempted (G-1, design doc 03 screen 6).
        email_attempted: !!clubContactEmail,
        email_sent: !!clubContactEmail,
        token,
      },
    };
  },
  // Public verify flow.
  getVerify: async (token) => {
    await wait();
    if (String(token).includes("expired")) {
      return { success: false, status: 410, error: "This link has expired." };
    }
    return {
      success: true,
      data: {
        // Verification-flow status (not a Pitch status): the club hasn't
        // confirmed yet. The page drives its steps from HTTP codes, not this.
        status: "pending",
        // The club sees ITS OWN request here (Rule 8 exemption for the club).
        tender: {
          tender_category: "player",
          position: "center_forward",
          type_of_tender: "buy",
          recruiting_for: "professional",
          transfer_window: "Summer 2026",
          commission_split: "60 / 40",
          commission_split_note: "",
        },
        posted_agency: AGENCIES[1],
        responsible_agent: AGENTS[1],
      },
    };
  },
  submitPhone: async (token, phone) => {
    await wait();
    if (!phone || phone.length < 6) {
      return { success: false, status: 400, error: "Please enter a valid phone number." };
    }
    return { success: true, data: { masked_phone: `+34 ••• ••• ${phone.slice(-3)}` } };
  },
  confirmOtp: async (token, otp) => {
    await wait();
    if (otp === "000000") {
      return { success: false, status: 429, error: "Too many attempts." };
    }
    if (!otp || otp.length !== 6) {
      return { success: false, status: 400, error: "That code isn't correct." };
    }
    return { success: true, data: { verification_status: "club_verified" } };
  },
  decline: async (token, reason) => {
    await wait();
    return { success: true, data: { status: "declined", reason: reason || "" } };
  },

  // ── Phase 1.5: response pipeline ──────────────────────────────────────────
  pitch: async (id, payload) => {
    await wait();
    return {
      success: true,
      data: { id: Math.floor(Math.random() * 9000) + 500, tender: id, status: "active", ...payload },
    };
  },
  getResponses: async (params) => {
    await wait();
    const p = params instanceof URLSearchParams ? params : new URLSearchParams(params || {});
    let out = [...RESPONSES];
    const tender = p.get("tender");
    const status = p.get("status");
    const shortlisted = p.get("shortlisted");
    if (tender) out = out.filter((r) => String(r.tender?.id) === String(tender));
    if (status) out = out.filter((r) => r.status === status);
    if (shortlisted === "true") out = out.filter((r) => r.is_shortlisted);
    out.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return { success: true, data: out, pagination: paginate(out) };
  },
  // `status` is the exact backend value: "accepted" or "rejected".
  setResponseStatus: async (pitchId, status) => {
    await wait();
    let updated = null;
    RESPONSES = RESPONSES.map((r) => {
      if (String(r.id) !== String(pitchId)) return r;
      updated = { ...r, status };
      return updated;
    });
    // Accept opens the anonymized chat channel first; backend returns the id.
    const channel =
      status === "accepted" ? { channel_id: `pitch-${pitchId}`, channel_type: "messaging" } : undefined;
    return { success: true, data: { id: Number(pitchId), status, ...(channel ? { channel } : {}) } };
  },
  setResponseShortlist: async (pitchId, on) => {
    await wait();
    let updated = null;
    RESPONSES = RESPONSES.map((r) => {
      if (String(r.id) !== String(pitchId)) return r;
      updated = { ...r, is_shortlisted: !!on };
      return updated;
    });
    return { success: true, data: updated };
  },
};

export default agentTenderMock;
